import dayjs from 'dayjs';

export {
  parseInteger,
  parseDecimal,
  parseBoolean,
  parseDate,
  parseTimestamp,
  parseString,
  resolveNumberFormat,
  resolveDateFormat,
  hexToRgba,
  parseCoordinate,
  isBlank,
  NUMBER_FORMATS,
  DATE_FORMATS,
  INVALID_NUMBER,
  INVALID_COORDINATE,
  rgbFromHex
};

const THOUSAND_SEPARATOR_REGEXP = { 
  ',': /^\d{1,3}(,\d{3})*$/,
  '.': /^\d{1,3}(\.\d{3})*$/,
  ' ': /^\d{1,3}(\s\d{3})*$/
}
const NON_DIGIT_REGEXP = /[\s\.,]/



const NUMBER_FORMATS = {
  default: {
    example: '1 000.0…',
    thousandSeparator: ' ',
    decimalSeparator: '.',
    validation: /^-?\d{1,3}(\s\d{3})*(\.\d*){0,1}$/
  },
  format_1: {
    example: '1 000,0…',
    thousandSeparator: ' ',
    decimalSeparator: ',',
    validation: /^-?\d{1,3}(\s\d{3})*(,\d*){0,1}$/
  },
  format_2: {
    example: '1,000.0…',
    thousandSeparator: ',',
    decimalSeparator: '.',
    validation: /^-?\d{1,3}(,\d{3})*(\.\d*){0,1}$/
  },
  format_3: {
    example: '1.000,0…',
    thousandSeparator: '.',
    decimalSeparator: ',',
    validation: /^-?\d{1,3}(\.\d{3})*(,\d*){0,1}$/
  },
  format_4: {
    example: '1000,0',
    thousandSeparator: null,
    decimalSeparator: ',',
    validation: /^-?\d*(,\d*){0,1}$/
  },
  format_5: {
    example: '1000.0',
    thousandSeparator: null,
    decimalSeparator: '.',
    validation: /^-?\d*(\.\d*){0,1}$/
  },
  coordinate_decimal: { // Limit in -180. to 180.
    example: '100.000',
    thousandSeparator: null,
    decimalSeparator: '.',
    validation: /^-?(?:([1-9]?[0-9])|(1[0-7][0-9]|180))(\.\d+)?$/
  }
}

// List of common non-standard date time formats for dayjs
const DATE_FORMATS = [
  {
    example: () => dayjs().format('DD-MM-YYYY'),
    format: 'DD-MM-YYYY'
  },
  {
    example: () => dayjs().format('MM-DD-YYYY'),
    format: 'MM-DD-YYYY'
  },
  {
    example: () => dayjs().format('YYYY-MM-DD'),
    format: 'YYYY-MM-DD'
  },
  {
    example: () => dayjs().format('YYYY-DD-MM'),
    format: 'YYYY-DD-MM'
  },
  {
    example: () => dayjs().format('DD-MM-YYYY HH:mm'),
    format: 'DD-MM-YYYY HH:mm'
  },
  {
    example: () => dayjs().format('MM-DD-YYYY HH:mm'),
    format: 'MM-DD-YYYY HH:mm'
  },
  {
    example: () => dayjs().format('YYYY-MM-DD HH:mm'),
    format: 'YYYY-MM-DD HH:mm'
  },
  {
    example: () => dayjs().format('YYYY-DD-MM HH:mm'),
    format: 'YYYY-DD-MM HH:mm'
  }
];

const INVALID_BOOLEAN = 'Invalid boolean';
const INVALID_NUMBER = 'Invalid number';
const UNKNOWN_FORMAT = 'UNKNOWN_FORMAT';
const INVALID_COORDINATE = 'Invalid coordinate';
const AUTO = 'auto';

const parseBoolean = ({value, inputFormat=AUTO}) => {
  if (isBlank(value)) return null;

  if (typeof value === 'boolean') return value;

  if (typeof value === 'number' || !isNaN(value)) {
    if (Number(value) === 1) return true;
    if (Number(value) === 0) return false;
  }

  if (typeof value === 'string') {
    value = value.trim()
    if(value === '1') return true;
    if(value === '0') return false;
    if(value.toLowerCase() === 'yes') return true;
    if(value.toLowerCase() === 'no') return false;
    if(value.toLowerCase() === 'true') return true;
    if(value.toLowerCase() === 'false') return false;
  }

  return INVALID_BOOLEAN;
}

const parseDate = ({value, inputFormat=AUTO, outputFormat = 'YYYY-MM-DD'}) => {
  if (isBlank(value)) return null;
  if (typeof value == 'number') {
    // Expected to be a serial format (days from 1900-01-00, i.e. what excel stores)
    value = new Date(Date.UTC(0, 0, value - 1));
  }
  if (inputFormat == AUTO) return dayjs(value).format(outputFormat);
  return dayjs(value, inputFormat).format(outputFormat);
}

const parseTimestamp = ({value, inputFormat=AUTO, outputFormat='YYYY-MM-DD HH:mm Z'}) => {
  return parseDate({value, inputFormat, outputFormat})
}

const parseDecimal = ({value, inputFormat=AUTO}) => {
  if (isBlank(value)) return null;

  let normalizedValue = value;
  if (typeof value === 'string') {
    normalizedValue = value.trim();
    let format = inputFormat;
    if (format == AUTO) {
      format = resolveNumberFormat(normalizedValue);
      if (format == UNKNOWN_FORMAT) return INVALID_NUMBER;
    } else {
      if (!NUMBER_FORMATS[format]?.validation?.test(normalizedValue))
        return INVALID_NUMBER;
    }

    const {thousandSeparator, decimalSeparator} = NUMBER_FORMATS[format];
    normalizedValue = normalizedValue.replaceAll(thousandSeparator, '').replace(decimalSeparator, '.');
  }

  if (isNaN(normalizedValue)) return INVALID_NUMBER
  return Number(normalizedValue)
}


// Same as decimal parser, but rounds of fraction
const parseInteger = (params) => {
  let value = parseDecimal(params)
  if (isBlank(value)) return null;

  if (isNaN(value)) return value // Invalid value
  
  return Math.round(value)
}

const parseString = ({value, inputFormat=AUTO}) => {
  if (isBlank(value)) return '';
  return ('' + value).trim();
}

const parseCoordinate = ({value}) => {
  const convertedValue = toArrayIfPossible(value)
  if (isBlank(convertedValue)) return null;
  const strCoord = ('' + convertedValue).trim().split(',');
  let res = [];
  if (strCoord.length == 2 && strCoord.every((s) => s !== "")){
    res = strCoord.map((v) => parseDecimal({value: v, inputFormat: 'coordinate_decimal'}));
  } else {
    return INVALID_COORDINATE;
  }
  return res.join(',').trim();
}

const isBlank = (value) => {
  if (typeof value == 'undefined') return true;
  if (value === null) return true;
  if (typeof value == 'string' && value.trim() === '') return true;
  return false;
}

const validThousandSeparators = (value, separator) => {
  try {
    return THOUSAND_SEPARATOR_REGEXP[separator].test(value);
  } catch {
    return false;  
  }
}

const rgbFromHex = (hex) => {
  hex = hex.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  return [r, g, b];
}

const hexToRgba = (hex, opacity) => {
  let [r, g, b] = rgbFromHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/* 
 * Try to resolve the format of NUMBER_FORMATS. 
 * !! Fragile function as it's impossible to tell whether "100.010" is 100000 or 100.010
 * Returns UNKNONW_FORMAT if can't resolve 
 *
 * See bottom of the page for expected results
*/
const resolveNumberFormat = (value) => {
  // Remove negation & get rest non-numbers
  const absValue = value.replace(/^\-/, '')
  const nonNumbers = absValue.replace(/\d/g,'');
  let thousandSeparator = null;
  let decimalSeparator = null;
  // Check only for comma, dot & space
  if (nonNumbers.length > 0 && !NON_DIGIT_REGEXP.test(nonNumbers)) return UNKNOWN_FORMAT;
  let allSame = !nonNumbers.split(nonNumbers[0]).some(s => s);

  // If all same & more than 1, must be thousand separator
  if ( allSame && nonNumbers.length > 1) {
    thousandSeparator = nonNumbers[0];
  } 
  // Could be either thousand or decimal, try to figure out
  else if ( allSame && nonNumbers.length == 1) {
    if (nonNumbers[0] == ' ') { // WS only for thousand separators
      thousandSeparator = ' ';
    }
    // It's the 4th char from the end. Can't tell, could be either
    else if (absValue.slice(-4,-3) == nonNumbers[0]) {
      return UNKNOWN_FORMAT 
    }
    else { // Otherwise it must be a decimal separator
      decimalSeparator = nonNumbers[0]
    }
  }
  // If all are not same, only the last can be different
  else if (!nonNumbers.slice(0, -1).split(nonNumbers[0]).some(s => s)) {
    thousandSeparator = nonNumbers[0]
    decimalSeparator = nonNumbers.slice(-1)
  } 
  else {
    return UNKNOWN_FORMAT
  }

  // Validate thousand separator positions
  if (thousandSeparator && !validThousandSeparators(absValue.split(decimalSeparator)[0], thousandSeparator)) return UNKNOWN_FORMAT;

  if (decimalSeparator) {
    return Object.keys(NUMBER_FORMATS).find(f => 
      NUMBER_FORMATS[f].thousandSeparator == thousandSeparator && 
      NUMBER_FORMATS[f].decimalSeparator == decimalSeparator
    );
  }
  // No decimal separator in given number, return the first with thousand separator
  return Object.keys(NUMBER_FORMATS).find(f =>  NUMBER_FORMATS[f].thousandSeparator == thousandSeparator);

}

const resolveDateFormat = (value) => {
  const matches = [];

  for (const { format } of DATE_FORMATS) {
    const parsed = dayjs(value, format, true); // strict parsing
    if (parsed.isValid()) {
      matches.push(format);
      if (matches.length > 1) return undefined; // ambiguity
    }
  }

  return matches.length === 1 ? matches[0] : undefined;
}

const toArrayIfPossible = (str) => {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : str;
  } catch (e) {
    return str;
  }
}

/*

const expectResult = (func, input, expectedResult) => {
  var result = func(input);
  if (result == expectedResult) {
    console.warn(`Success`, input);
  } else {
    console.warn(`Expected ${expectedResult} for`, input, `got ${result}`)
  }
}

// [Input, ExpectedOutput]
const resolveNumberFormatTests = [
  ['1 000.0', 'default'],
  ['1 000,0', 'format_1'],
  ['1,000.0', 'format_2'],
  ['1.000,0', 'format_3'],
  ['1000,0', 'format_4'],
  ['1000.0', 'format_5'],
  ['-1 000.0', 'default'],
  ['-1 000,0', 'format_1'],
  ['-1,000.0', 'format_2'],
  ['-1.000,0', 'format_3'],
  ['-1000,0', 'format_4'],
  ['-1000.0', 'format_5'],
  ['1 000', 'default'],
  ['1,000,000', 'format_2'],
  ['1.000.000', 'format_3'],
  ['1,000', UNKNOWN_FORMAT],
  ['1.000', UNKNOWN_FORMAT],
  ['1000', 'format_4'],
  ['100,000', UNKNOWN_FORMAT],
  ['100.000', UNKNOWN_FORMAT],
  ['1,00', 'format_4'],
  ['1.00', 'format_5'],
  ['100/ 000', UNKNOWN_FORMAT],
];

const parseDecimalTests = [
  // Auto formats
  [{value: '1 000.0'}, 1000],
  [{value: '1 000,1'}, 1000.1],
  [{value: '1,000.0'}, 1000],
  [{value: '1.000,1'}, 1000.1],
  [{value: '1000,1'}, 1000.1],
  [{value: '1000,1234'}, 1000.1234],
  [{value: '1000.0'}, 1000],
  [{value: '1,000,000'}, 1000000],
  [{value: '-1 000.0'}, -1000],
  [{value: '-1 000,1'}, -1000.1],
  [{value: '1,000'}, INVALID_NUMBER],
  [{value: '100.000'}, INVALID_NUMBER],
  [{value: '1.00'}, 1],
  [{value: '12030320230230023233232323232232323.003424324324324432432432'}, 12030320230230023233232323232232323.003424324324324432432432],
  // Defined formats
  [{value: '1 000,0', inputFormat: 'format_1'}, 1000],
  [{value: '1.000,0', inputFormat: 'format_1'}, INVALID_NUMBER],
  [{value: '1.000.000', inputFormat: 'format_3'}, 1000000],
];

// Integer parser also uses decimal parser, hence these test both
const parseIntegerTests = [
  // Anything with extra chars is ignored
  [{value: '1€'}, INVALID_NUMBER],
  [{value: 'not anumber'}, INVALID_NUMBER],
  // Blanks are considered null
  [{value: '  '}, null],
  [{value: ''}, null],
  [{value: undefined}, null],
  [{value: null}, null],
  [{value: '1 000.0'}, 1000],
  [{value: '1 000,003424324324324432432432'}, 1000],
  [{value: '1,233,000.0000023'}, 1233000],
]

const parseBooleanTests = [
  [{value: true}, true],
  [{value: false}, false],

  // Incase sensitive strings
  [{value: 'true'}, true],
  [{value: 'false'}, false],
  [{value: 'trUe'}, true],
  [{value: 'falSe'}, false],
  [{value: 'YEs'}, true],
  [{value: 'No'}, false],
  // 1 & 0 consired true false
  [{value: 1}, true],
  [{value: 0}, false],
  [{value: '1'}, true],
  [{value: '0'}, false],
  // Values are trimmed
  [{value: ' 0'}, false],
  [{value: '1 '}, true],

  [{value: 'tru '}, INVALID_BOOLEAN],
  [{value: '2 '}, INVALID_BOOLEAN],
  [{value: 2}, INVALID_BOOLEAN],
  [{value: null}, null],
]

const parseStringTests = [
  // Trimming
  [{value: 'a '}, 'a'],
  [{value: null}, ''],
  // Basic data types
  [{value: 1}, '1'],
  [{value: 1.1}, '1.1'],
  [{value: true}, 'true'],
  [{value: 1321312.3223233232}, '1321312.3223233232'],

]


resolveNumberFormatTests.forEach((a) => expectResult(resolveNumberFormat, a[0], a[1]));
parseDecimalTests.forEach((a) => expectResult(parseDecimal, a[0], a[1]));
parseIntegerTests.forEach((a) => expectResult(parseInteger, a[0], a[1]));
parseBooleanTests.forEach((a) => expectResult(parseBoolean, a[0], a[1]));
parseStringTests.forEach((a) => expectResult(parseString, a[0], a[1]));

*/